import {
  type IKnownBusinessEntityContact,
  type IUserSignupDetails
} from "@/interfaces/api/known-business-entity-contact.interface.ts"
import { BaseModel } from "@/models/base.model"
import { Person } from "@/models/person.model"
import { KnownBusinessEntity } from "@/models/known-business-entity.model.ts"
import { type Role } from "@/models/role/role.model"
import { getSubclassForRoleType } from "@/helper/role-helper"
import type { RoleTypes } from "@/enums/role-types"
import { ModelFactory } from "@/factories/model-factory"
import type { RoleServiceProvider } from "@/models/role/role-service-provider.model"
import type { RolePlanner } from "@/models/role/role-planner.model"
import { type ModelConstructorSignature } from "@/models/types/model-constructor-signature"
import { normalizePhoneNumber } from "@/helper/phone-number-helper.ts"
import { AvailabilityBucket } from "@/models/availability-bucket.model.ts"
import type { RoleFinancialOfficer } from "@/models/role/role-financial-officer.model.ts"

export class KnownBusinessEntityContact extends BaseModel implements IKnownBusinessEntityContact {
  public static className = "KnownBusinessEntityContact" // Used for socket communication.
  public static verboseName = "persoon en bedrijf"

  public static override managedResolveFields = ["person", "knownBusinessEntity"]

  id: string
  person: Person
  knownBusinessEntity?: KnownBusinessEntity | null
  userId: string
  userSignupDetails?: IUserSignupDetails | null

  email?: string
  phoneNumber?: string
  isPrivateIndividual: boolean
  isActive: boolean
  invitationCode?: string

  roles: Role[]
  availabilityBucket: AvailabilityBucket

  constructor(knownBusinessEntityContact: IKnownBusinessEntityContact, managed = false) {
    super(knownBusinessEntityContact)

    this.id = knownBusinessEntityContact.id
    this.person = ModelFactory.childModel(Person, knownBusinessEntityContact.person, managed)
    this.knownBusinessEntity = ModelFactory.childModel(
      KnownBusinessEntity,
      knownBusinessEntityContact.knownBusinessEntity,
      managed
    )
    this.userId = knownBusinessEntityContact.userId
    this.userSignupDetails = knownBusinessEntityContact.userSignupDetails ?? null

    this.email = knownBusinessEntityContact.email
    this.phoneNumber = knownBusinessEntityContact.phoneNumber
    this.isPrivateIndividual = knownBusinessEntityContact.isPrivateIndividual
    this.isActive = knownBusinessEntityContact.isActive
    this.invitationCode = knownBusinessEntityContact.invitationCode

    this.availabilityBucket = ModelFactory.childModel(
      AvailabilityBucket,
      knownBusinessEntityContact.availabilityBucket,
      managed
    )

    this.roles = []
    if (knownBusinessEntityContact.roles) {
      for (const role of knownBusinessEntityContact.roles) {
        const Subclass = getSubclassForRoleType(role.type)
        this.roles.push(
          ModelFactory.createModel(
            Subclass as ModelConstructorSignature<any, any>,
            role,
            managed
          ) as Role
        )
      }
    }
  }

  public hasRole(role: RoleTypes): boolean {
    return this.roles.some((r) => r.type === role)
  }

  /** Whether a user account is associated with this contact. */
  public get hasUser(): boolean {
    return !!this.userId
  }

  // Overloads to associate the correct return type with the role type
  public getRole(roleType: RoleTypes.SERVICE_PROVIDER): RoleServiceProvider | undefined
  public getRole(roleType: RoleTypes.PLANNER): RolePlanner | undefined
  public getRole(roleType: RoleTypes.FINANCIAL_OFFICER): RoleFinancialOfficer | undefined

  // Get the role of the given type
  public getRole(role: RoleTypes) {
    return this.roles.find((r) => r.type === role)
  }

  public getDisplayText() {
    return `${this.person?.fullName || "Onbekend"} ${this.knownBusinessEntityName}`
  }

  public get knownBusinessEntityName(): string {
    if (this.knownBusinessEntity) {
      return this.knownBusinessEntity.name
    }
    return this.isPrivateIndividual ? " Particulier" : "Onbekend"
  }

  public getNormalizedPhoneNumber() {
    if (this.phoneNumber) {
      return normalizePhoneNumber(this.phoneNumber)
    }
    return ""
  }
}
